import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FiClock, FiTag, FiChevronRight, FiThumbsUp, FiThumbsDown, FiPrinter, FiShare2 } from '../../components/Icons';
import { getArticle } from '../../utils/articles';
import Layout from '../../components/Layout/Layout';
import '../../styles/ArticlePage.css';

const ArticlePage: React.FC = () => {
  const { category, slug } = useParams<{ category: string; slug: string }>();
  const [wasHelpful, setWasHelpful] = useState<boolean | null>(null);
  
  // In a real app, this would fetch from a database or CMS
  const article = getArticle(category || '', slug || '');

  if (!article) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Article Not Found</h1>
            <p className="text-gray-600 mb-8">
              The article you're looking for doesn't exist or has been moved.
            </p>
            <Link
              to="/"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Return to Home
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const handlePrint = () => {
    window.print();
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: article.title,
        text: article.description,
        url: window.location.href,
      });
    } else {
      // Fallback to copying URL
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  const handleHelpful = (helpful: boolean) => {
    setWasHelpful(helpful);
    // In a real app, this would send feedback to the backend
    console.log('Article feedback:', helpful ? 'helpful' : 'not helpful');
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="flex items-center space-x-2 text-sm text-gray-500 mb-8">
          <Link to="/" className="hover:text-gray-700">Home</Link>
          <FiChevronRight className="h-4 w-4" />
          <Link to={`/categories/${category}`} className="hover:text-gray-700 capitalize">
            {category}
          </Link>
          <FiChevronRight className="h-4 w-4" />
          <span className="text-gray-900">{article.title}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <article className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              {/* Article Header */}
              <header className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-4">
                  {article.title}
                </h1>
                <p className="text-lg text-gray-600 mb-4">
                  {article.description}
                </p>
                <div className="flex items-center space-x-4 text-sm text-gray-500">
                  {article.readTime && (
                    <span className="flex items-center space-x-1">
                      <FiClock className="h-4 w-4" />
                      <span>{article.readTime} min read</span>
                    </span>
                  )}
                  <span className="flex items-center space-x-1">
                    <FiTag className="h-4 w-4" />
                    <span className="capitalize">{article.category}</span>
                  </span>
                  <span>
                    Last updated: {new Date(article.lastUpdated).toLocaleDateString()}
                  </span>
                </div>
              </header>

              {/* Article Actions */}
              <div className="flex items-center space-x-4 mb-8 pb-8 border-b border-gray-200">
                <button
                  onClick={handlePrint}
                  className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
                >
                  <FiPrinter className="h-4 w-4" />
                  <span>Print</span>
                </button>
                <button
                  onClick={handleShare}
                  className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
                >
                  <FiShare2 className="h-4 w-4" />
                  <span>Share</span>
                </button>
              </div>

              {/* Article Content */}
              <div 
                className="article-content"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {/* Feedback Section */}
              <div className="mt-12 pt-8 border-t border-gray-200">
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Was this article helpful?
                  </h3>
                  {wasHelpful === null ? (
                    <div className="flex items-center justify-center space-x-4">
                      <button
                        onClick={() => handleHelpful(true)}
                        className="flex items-center space-x-2 px-4 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors"
                      >
                        <FiThumbsUp className="h-5 w-5" />
                        <span>Yes</span>
                      </button>
                      <button
                        onClick={() => handleHelpful(false)}
                        className="flex items-center space-x-2 px-4 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors"
                      >
                        <FiThumbsDown className="h-5 w-5" />
                        <span>No</span>
                      </button>
                    </div>
                  ) : (
                    <div className="text-green-600">
                      Thank you for your feedback!
                    </div>
                  )}
                </div>
              </div>
            </article>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            {/* Table of Contents */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6 sticky top-24">
              <h3 className="font-semibold text-gray-900 mb-4">In this article</h3>
              <nav className="space-y-2">
                {article.tableOfContents?.map((item, index) => (
                  <a
                    key={index}
                    href={`#${item.id}`}
                    className="block text-sm text-gray-600 hover:text-gray-900 pl-4 border-l-2 border-gray-200 hover:border-blue-600 transition-colors"
                  >
                    {item.title}
                  </a>
                ))}
              </nav>
            </div>

            {/* Related Articles */}
            {article.relatedArticles && article.relatedArticles.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Related Articles</h3>
                <div className="space-y-3">
                  {article.relatedArticles.map((relatedSlug) => (
                    <Link
                      key={relatedSlug}
                      to={`/articles/${category}/${relatedSlug}`}
                      className="block text-sm text-blue-600 hover:text-blue-800"
                    >
                      {/* This would be fetched from the database */}
                      {relatedSlug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Need Help Card */}
            <div className="bg-blue-50 rounded-lg p-6 mt-6">
              <h3 className="font-semibold text-gray-900 mb-2">Need more help?</h3>
              <p className="text-sm text-gray-600 mb-4">
                Contact our support team for personalized assistance.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Contact Support
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </Layout>
  );
};

export default ArticlePage;